import React, { createContext, useContext } from 'react';
import { DezzyCompiler } from './compiler';
import { DezzyProviderProps, DezzyViewProps, SectionRegistry } from './types';

const DezzyContext = createContext<{
  compiler: DezzyCompiler;
  registry: SectionRegistry;
  getSection: (id: string) => React.ComponentType<any> | null;
} | null>(null);

export const DezzyProvider: React.FC<DezzyProviderProps> = ({ 
  registry = {}, 
  children 
}) => {
  const compiler = new DezzyCompiler(registry);
  
  const getSection = (id: string): React.ComponentType<any> | null => {
    return registry[id] || null;
  };

  return (
    <DezzyContext.Provider value={{ compiler, registry, getSection }}>
      {children}
    </DezzyContext.Provider>
  );
};

export const DezzyView: React.FC<DezzyViewProps> = ({ 
  dsl, 
  ctx = {}, 
  actions = {} 
}) => {
  const context = useContext(DezzyContext);
  if (!context) {
    throw new Error('DezzyView must be used within a DezzyProvider');
  }
  const { compiler } = context;
  
  const ViewComponent = compiler.compileView(dsl);
  
  return <ViewComponent ctx={ctx} actions={actions} />;
};

export const useDezzy = () => {
  const context = useContext(DezzyContext);
  if (!context) {
    throw new Error('useDezzy must be used within a DezzyProvider');
  }
  return context;
};

// Higher-order component for creating section components
export function createSection<T = any>(
  component: React.ComponentType<T>
): React.ComponentType<T & { variant?: string }> {
  return ({ variant = 'default', ...props }: T & { variant?: string }) => {
    const restProps = props as T;
    return React.createElement('div', {
      className: `dezzy-section dezzy-section--${variant}`,
      'data-variant': variant,
    }, React.createElement(component as React.ComponentType<any>, restProps));
  };
}